<template>
    <!-- Pagination Details -->
    <DataView v-if="pagination" :value="tours" :layout="layout" :rows="pagination.perPage"
        :total-records="pagination.total" current-page-report-template="Showing {first} to {last} of {totalRecords}"
        data-key="id" :first="pagination.perPage * pagination.currentPage -
            (pagination.perPage + 1)
            " lazy @update:first="0">
        <template #grid="{ items }">
            <div class="grid grid-cols-2 gap-2 md:gap-3 lg:grid-cols-4">
                <div v-for="item, idx in items" class="my-3">
                    <default-item :tour="item" :loading="loading" layout="grid" :url="getRoute(item)"
                        :priority="idx < 4" v-model:height="maxHeight"
                        @click="registerProductClick(item, idx + 1, location)"
                        @impression="registerProductImpression(item, idx + 1, location)" />
                </div>
            </div>
        </template>

        <template #list="{ items }">
            <div class="flex items-center justify-center">
                <div class="flex flex-col w-full gap-2 py-10 md:w-3/4">
                    <default-item v-for="item, idx in items" :tour="item" :loading="loading" layout="list"
                        :url="getRoute(item)" :priority="idx < 2"
                        @click="registerProductClick(item, idx + 1, location)"
                        @impression="registerProductImpression(item, idx + 1, location)" />
                </div>
            </div>
        </template>

        <template #header>
            <div class="flex items-center justify-between">
                <div class="text-xs">
                    {{ `Showing ${pagination.count} of ${pagination.total}` }}
                </div>

                <div class="flex items-center flex-wrap gap-2">
                    <p-button @click="toggleLayout" size="small" :icon="layout == 'grid' ? 'pi pi-list' : 'pi pi-th-large'
                        " severity="secondary" class="text-xs" :label="layout == 'grid' ? 'List' : 'Grid'" />

                    <p-select :options="order_by_options" @change="onSort" v-model="order_by" option-value="slug"
                        option-label="display_as" size="small" class="text-xs" />
                </div>
            </div>
        </template>

        <template #footer>
            <div class="flex items-center justify-center w-full">
                <div v-if="pagination.totalPages > pagination.currentPage"
                    class="flex flex-col items-center justify-center w-full md:w-1/2">
                    <div class="text-sm font-thin">
                        {{
                            `Showing ${pagination.count} of ${pagination.total}`
                        }}
                    </div>
                    <p-button fluid :label="`Show the next ${pagination.currentPage == pagination.totalPages - 1
                        ? pagination.total - pagination.count
                        : pagination.perPage
                        } tours`" @click="onPage" :loading="loadingContent" :disabled="loadingContent" />
                </div>
            </div>
        </template>
    </DataView>

    <div v-else class="flex p-20 items-center justify-center">
        <progress-bar class="w-[50vw] lg:w-[25vw]" mode="indeterminate" style="height: 2px" />
    </div>
</template>

<script setup lang="ts">
import { DataView, Button as PButton, Select as PSelect, ProgressBar } from "primevue";
import { Location, OrderByOption, Pagination, Tour, Tag } from "@/types";
import { onMounted, ref } from "vue";
import { useApiProxy } from "@/Hook/useProxy";
import DefaultItem from "./Item/DefaultItem.vue";
import { usePage } from "@inertiajs/vue3";
import { SelectChangeEvent } from "primevue/select";
import { useSession } from "@/Hook/useSession";
import useAgency from "@/Hook/useAgency";
import useDataLayer from "@/Hook/useDataLayer";


const { loading: loadingContent, request } = useApiProxy();

const props = defineProps<{
    loading?: boolean;
    tours?: Tour[];
    pagination?: Pagination;
    meta?: {
        tour_categories: Tag[];
        tour_count_per_category?: Record<string, number>;
        min_price: number;
        max_price: number;
        min_duration: number;
        max_duration: number;
        min_rating: number;
        max_rating: number;
    }
    | any;
    location?: Location;
    data?: { [key: string]: any };
}>();

const emit = defineEmits(["order_change", "filtered"]);

const { registerProductImpression, registerProductClick } = useDataLayer()

const maxHeight = ref()

const order_by_options = ref<OrderByOption[]>([]);

const order_by = ref<string>();

const pagination = ref(props.pagination);

const tours = ref(props.tours ?? []);

const session = useSession()

const layout = ref<"list" | "grid">(session.get('tours_layout', 'grid') ?? 'grid');

const reseller = useAgency()

const toggleLayout = function () {
    layout.value = layout.value == 'grid' ? 'list' : 'grid';
    session.set('tours_layout', layout.value);
}

const onSort = function (e: SelectChangeEvent) {
    emit("order_change", e.value);
};

const onPage = function () {
    const page = (pagination.value?.currentPage ?? 0) + 1;

    request(
        getSearchRoute({
            ...(props.data ?? usePage().props.__params), page,
            json: true
        }),
        {
            method: "GET",
        }
    ).then(function (data: { tours: Tour[]; pagination: Pagination; meta: any }) {
        tours.value = [...tours.value, ...data.tours];
        pagination.value = {
            ...(pagination.value ?? data.pagination),
            currentPage: page,
            count: tours.value.length,
        };
        emit('filtered', data);
    });
};

const getRoute = function (tour: Tour) {
    const params: Record<string, any> = { id: tour.id, tour_slug: tour.slug };

    const specific = usePage().props?.specific_tours;

    if (reseller.hasAgency.value) {
        params['agent_id'] = reseller.agent.value
        params['agency_id'] = reseller.agencyId.value
    }

    if (props.location) {
        if (props.location.region)
            return route(specific ? 'specific.show.tour.region' : "show.tour.region", {
                ...params,
                country: props.location.country_slug,
                region: props.location.region_slug,
            });
        else
            return route(specific ? 'specific.show.tour.country' : "show.tour.country", {
                ...params,
                country: props.location.country_slug,
            });
    } else return route(specific ? "specific.show.tour" : "show.tour", params);
};

const getSearchRoute = function (params: Record<string, any> = {}) {

    if (reseller.hasAgency.value) {
        params['agent_id'] = reseller.agent.value
        params['agency_id'] = reseller.agencyId.value
    }

    const specific = usePage().props?.specific_tours;
    const routeName = specific ? "specific.tours" : "tours"

    if (props.location) {
        if (props.location.region)
            return route(routeName, {
                ...params,
                country: props.location.country_slug,
                region: props.location.region_slug,
            });
        else
            return route(routeName, {
                ...params,
                country: props.location.country_slug,
            });
    } else return route(routeName, params);
}

onMounted(function () {
    const params = usePage().props.__params;
    order_by_options.value = usePage().props.order_by_options;
    order_by.value =
        params["order_by"] ??
        order_by_options.value.find((sort) => sort.is_default)?.slug;


    if (!props.tours)
        onPage();
});
</script>
